import type { ExchangeRateQueryPort } from "../../../application/ports/ExchangeRateQueryPort";

interface ExchangeRateHttpAdapterConfig {
  baseUrl: string;
  authorizationToken: string;
}

interface BffResponse<T> {
  success: boolean;
  data: T;
}

const EXCHANGE_RATES_PATH = "payments/exchange-rates";
const TARGET_CURRENCY = "VES";

export class ExchangeRateHttpAdapter implements ExchangeRateQueryPort {
  constructor(private readonly config: ExchangeRateHttpAdapterConfig) {}

  async getUsdRate(): Promise<number> {
    const url = this.composeUrl(EXCHANGE_RATES_PATH, {
      currency: TARGET_CURRENCY,
    });

    const response = await fetch(url, {
      headers: this.buildHeaders(),
    });

    if (!response.ok) {
      throw new Error("No fue posible obtener la tasa de cambio");
    }

    const rawPayload = (await response.json()) as
      | BffResponse<{ rate?: number }>
      | { rate?: number };
    const payload = this.unwrapBffData(rawPayload);
    const rate = payload.rate;

    if (typeof rate !== "number" || Number.isNaN(rate)) {
      throw new Error("Tasa de cambio inválida");
    }

    return rate;
  }

  private composeUrl(path: string, params: Record<string, string>): string {
    const normalizedBase = this.config.baseUrl.endsWith("/") ? this.config.baseUrl : `${this.config.baseUrl}/`;
    const url = new URL(path, normalizedBase);

    Object.entries(params).forEach(([key, value]) => {
      url.searchParams.append(key, value);
    });

    return url.toString();
  }

  private unwrapBffData<T>(payload: T | BffResponse<T>): T {
    if (this.isBffResponse(payload)) {
      return payload.data;
    }

    return payload;
  }

  private isBffResponse<T>(payload: T | BffResponse<T>): payload is BffResponse<T> {
    return (
      typeof payload === "object" &&
      payload !== null &&
      "success" in payload &&
      "data" in payload
    );
  }

  private buildHeaders(): HeadersInit {
    return {
      Accept: "application/json",
      Authorization: `${this.config.authorizationToken}`,
    };
  }
}
